import { api } from './client';

/**
 * Web Push (PWA) subscription client. Ported from Book Magic, adapted to
 * Goodtime's {@link api} helper + playerId-in-body convention.
 *
 * The VAPID public key comes from {@code VITE_VAPID_PUBLIC_KEY}; until that's
 * set (and a service worker is registered by vite-plugin-pwa), every call here
 * is a clean no-op, so it's safe to wire up before push is fully provisioned.
 *
 * iOS note: Web Push only fires for a PWA added to the Home Screen (16.4+);
 * in a regular Safari tab `Notification`/`PushManager` may be absent — the
 * capability checks below handle that gracefully.
 */

// Public key — safe to ship to clients (it's the applicationServerKey). The
// matching private key is a server-only secret (VAPID_PRIVATE_KEY). Override
// per-env with VITE_VAPID_PUBLIC_KEY; the default is the dev keypair.
const VAPID_PUBLIC_KEY =
  (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined) ||
  'BKfBG72IsWBNiOKKiUDfmjW4ovjdCIzcTMO6Xe7dw6raVDnrrnNkAcRDi4ZM0Y6b7wRLsg8HUeVBhq5KcfEtZrs';

function pushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** True iff this device already holds a push subscription. */
export async function isPushEnabled(): Promise<boolean> {
  if (!pushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    return (await registration.pushManager.getSubscription()) != null;
  } catch {
    return false;
  }
}

/**
 * Prompt for permission, subscribe this device, and register the subscription
 * with the server so the player can be pushed (e.g. "your friend joined").
 * Returns true on success; false (no throw) when unsupported, denied, or
 * unconfigured — so callers can treat it as a best-effort enable.
 */
export async function enablePushNotifications(playerId: string): Promise<boolean> {
  if (!pushSupported()) return false;
  if (!VAPID_PUBLIC_KEY) {
    console.info('[push] disabled — VITE_VAPID_PUBLIC_KEY not configured');
    return false;
  }
  if (Notification.permission === 'denied') return false;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }
    await sendSubscriptionToServer(playerId, subscription);
    return true;
  } catch (e) {
    console.error('[push] failed to enable notifications', e);
    return false;
  }
}

/** Re-register an existing subscription with the server (e.g. on app load, so
 *  a player who already opted in stays subscribed after a key rotation). */
export async function refreshPushSubscription(playerId: string): Promise<void> {
  if (!pushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) await sendSubscriptionToServer(playerId, subscription);
  } catch (e) {
    console.error('[push] failed to refresh subscription', e);
  }
}

/** Unsubscribe this device locally (the server skips the stale endpoint on its
 *  next send). */
export async function disablePushNotifications(): Promise<void> {
  if (!pushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) await subscription.unsubscribe();
  } catch (e) {
    console.error('[push] failed to disable notifications', e);
  }
}

async function sendSubscriptionToServer(playerId: string, subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!p256dh || !auth) throw new Error('push subscription missing p256dh/auth keys');
  await api<void>('/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({ playerId, endpoint: subscription.endpoint, p256dh, auth }),
  });
}

/** URL-safe base64 VAPID key → Uint8Array for {@code applicationServerKey}.
 *  Pinned to an ArrayBuffer-backed array (TS 5.7's generic TypedArray). */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
